<template>
  <div class="notification-center">
    <v-badge :model-value="notifications.length > 0" :content="badgeContent" color="error" floating location="top end" offset-x="6" offset-y="6" class="notification-badge">
      <v-btn icon variant="text" class="notification-bell" aria-label="Abrir notificações" @click="dialog = true">
        <v-icon size="24">mdi-bell-outline</v-icon>
      </v-btn>
    </v-badge>

    <v-dialog v-model="dialog" max-width="860" scrollable transition="dialog-bottom-transition">
      <v-card class="notification-card">
        <div class="notification-header">
          <div class="notification-header__left">
            <div class="notification-icon-wrap">
              <v-icon size="22">mdi-bell-ring-outline</v-icon>
            </div>

            <div>
              <div class="notification-title">Central de notificações</div>
              <div class="notification-subtitle">{{ notifications.length }} notificação(ões)</div>
            </div>
          </div>

          <v-btn icon variant="text" aria-label="Fechar" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider />

        <v-card-text class="notification-body">
          <template v-if="loading">
            <div class="notification-empty">
              <v-progress-circular indeterminate size="28" />
              <span>Carregando notificações...</span>
            </div>
          </template>

          <template v-else-if="notifications.length">
            <div class="notification-toolbar">
              <div class="notification-counter">Notificação {{ currentIndex + 1 }} de {{ notifications.length }}</div>

              <div class="notification-nav">
                <v-btn variant="tonal" size="small" prepend-icon="mdi-chevron-left" :disabled="currentIndex === 0" @click="prevNotification"> Anterior </v-btn>

                <v-btn variant="tonal" size="small" append-icon="mdi-chevron-right" :disabled="currentIndex === notifications.length - 1" @click="nextNotification">
                  Próxima
                </v-btn>
              </div>
            </div>

            <v-sheet rounded="xl" class="notification-item">
              <div class="notification-meta">
                <v-chip size="small" color="primary" variant="flat"> Comunicado </v-chip>

                <span class="notification-date">
                  {{ formatDate(currentNotification?.dataCriacao) }}
                </span>
              </div>

              <h2 class="notification-item__title">
                {{ currentNotification?.titulo }}
              </h2>

              <div class="notification-item__content prose-content" v-html="currentNotification?.texto" />

              <!-- <div v-if="currentNotification?.local" class="notification-footer-info">Local: {{ currentNotification.local }}</div> -->
            </v-sheet>
          </template>

          <template v-else>
            <div class="notification-empty">
              <v-icon size="34">mdi-bell-off-outline</v-icon>
              <span>Nenhuma notificação disponível.</span>
            </div>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useNotificacoes, type Notificacao } from "~/composables/useNotificacoes";

const props = withDefaults(
  defineProps<{
    autoOpen?: boolean;
  }>(),
  {
    autoOpen: true,
  }
);

const route = useRoute();
const { fetchNotificacoes } = useNotificacoes();

const dialog = ref(false);
const loading = ref(false);
const notifications = ref<Notificacao[]>([]);
const currentIndex = ref(0);
const hasAutoOpened = ref(false);

const currentNotification = computed(() => notifications.value[currentIndex.value] || null);

const badgeContent = computed(() => {
  const total = notifications.value.length;
  return total > 9 ? "9+" : String(total);
});

const localAtual = computed(() => {
  return route.path;
});

const loadNotifications = async () => {
  loading.value = true;
  notifications.value = await fetchNotificacoes(localAtual.value);
  loading.value = false;

  if (currentIndex.value > notifications.value.length - 1) {
    currentIndex.value = 0;
  }
};

const prevNotification = () => {
  if (currentIndex.value > 0) currentIndex.value--;
};

const nextNotification = () => {
  if (currentIndex.value < notifications.value.length - 1) currentIndex.value++;
};

const formatDate = (date?: string) => {
  if (!date) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(date));
  } catch {
    return date;
  }
};

onMounted(async () => {
  await loadNotifications();

  if (props.autoOpen && notifications.value.length > 0 && !hasAutoOpened.value) {
    dialog.value = true;
    hasAutoOpened.value = true;
  }
});

watch(
  () => route.path,
  async () => {
    await loadNotifications();
  }
);

watch(dialog, async (opened) => {
  if (opened && !notifications.value.length) {
    await loadNotifications();
  }
});
</script>

<style scoped>
.notification-center {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 44px;
}

.notification-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.notification-badge :deep(.v-badge__wrapper) {
  overflow: visible;
}

.notification-badge :deep(.v-badge__badge) {
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.notification-bell {
  width: 44px;
  height: 44px;
  min-width: 44px;
  flex: 0 0 44px;
  border: 1px solid rgba(8, 47, 73, 0.12);
  background: rgba(255, 255, 255, 0.74);
  color: var(--funarpen-blue);
  transition:
    transform 0.2s ease,
    background-color 0.2s ease;
}

.notification-bell:hover {
  transform: translateY(-1px);
}

.notification-card {
  border-radius: var(--funarpen-radius);
  overflow: hidden;
}

.notification-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  background:
    linear-gradient(135deg, rgba(11, 79, 108, 0.1), rgba(191, 152, 81, 0.09)),
    rgba(248, 251, 253, 0.94);
}

.notification-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.notification-icon-wrap {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: var(--funarpen-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(11, 79, 108, 0.12);
  color: var(--funarpen-blue);
}

.notification-title {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.2;
}

.notification-subtitle {
  font-size: 0.92rem;
  opacity: 0.72;
  margin-top: 2px;
}

.notification-body {
  padding: 24px;
}

.notification-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.notification-counter {
  font-size: 0.95rem;
  font-weight: 600;
  opacity: 0.78;
}

.notification-nav {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.notification-item {
  padding: 24px;
  border: 1px solid rgba(8, 47, 73, 0.1);
  border-left: 5px solid var(--funarpen-gold);
  border-radius: var(--funarpen-radius) !important;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 251, 253, 0.94)),
    #fff;
}

.notification-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.notification-date {
  font-size: 0.9rem;
  opacity: 0.7;
}

.notification-item__title {
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 18px;
  color: var(--funarpen-navy);
}

.notification-item__content {
  font-size: 1rem;
  line-height: 1.75;
  color: rgba(16, 35, 52, 0.86);
  word-break: break-word;
}

.notification-footer-info {
  margin-top: 20px;
  padding-top: 14px;
  border-top: 1px solid rgba(8, 47, 73, 0.1);
  font-size: 0.88rem;
  opacity: 0.65;
}

.notification-empty {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex-direction: column;
  opacity: 0.7;
  text-align: center;
}

.prose-content :deep(p) {
  margin-bottom: 14px;
}

.prose-content :deep(strong) {
  font-weight: 800;
}

.prose-content :deep(blockquote) {
  margin: 18px 0;
  padding: 14px 18px;
  border-left: 4px solid var(--funarpen-gold);
  background: rgba(11, 79, 108, 0.06);
  border-radius: var(--funarpen-radius);
}

@media (max-width: 960px) {
  .notification-bell {
    width: 40px;
    height: 40px;
    min-width: 40px;
    flex-basis: 40px;
  }

  .notification-badge :deep(.v-badge__badge) {
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    font-size: 10px;
    line-height: 18px;
  }
}

@media (max-width: 768px) {
  .notification-header,
  .notification-body,
  .notification-item {
    padding: 18px;
  }

  .notification-item__title {
    font-size: 1.18rem;
  }

  .notification-toolbar {
    align-items: stretch;
  }

  .notification-nav {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 600px) {
  .notification-center {
    min-width: 38px;
  }

  .notification-bell {
    width: 38px;
    height: 38px;
    min-width: 38px;
    flex-basis: 38px;
  }

  .notification-badge :deep(.v-badge__badge) {
    min-width: 17px;
    height: 17px;
    padding: 0 4px;
    font-size: 9px;
    line-height: 17px;
  }

  .notification-header {
    align-items: flex-start;
  }

  .notification-title {
    font-size: 1rem;
  }

  .notification-subtitle {
    font-size: 0.84rem;
  }
}
</style>
